import React from 'react'
import { Select } from 'antd'
import { useIntl } from 'react-intl'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { IDepartmentPill } from '@vacationtracker/shared/types/team-energy'

interface IDepartmentSelectorProps {
  departments: IDepartmentPill[]
  selectedId: string | null
  onSelect: (id: string) => void
}

const PILL_THRESHOLD = 2

interface IOption {
  value: string
  label: string
  needAttentionCount: number
}

const DepartmentSelector: React.FC<IDepartmentSelectorProps> = ({ departments, selectedId, onSelect }) => {
  const { formatMessage } = useIntl()

  if (departments.length === 0) {
    return null
  }

  if (departments.length <= PILL_THRESHOLD) {
    return (
      <div className="te-pills">
        {departments.map((dept) => {
          const isActive = dept.id === selectedId
          return (
            <button key={dept.id} type="button" className={`te-pills__pill${isActive ? ' te-pills__pill--active' : ''}`} onClick={() => onSelect(dept.id)}>
              {dept.name}
              {dept.needAttentionCount > 0 && <span className="te-pills__badge">{dept.needAttentionCount}</span>}
            </button>
          )
        })}
      </div>
    )
  }

  const sortedDepartments = [...departments].sort((a, b) => {
    if (b.needAttentionCount !== a.needAttentionCount) {
      return b.needAttentionCount - a.needAttentionCount
    }
    return a.name.localeCompare(b.name)
  })

  const hasNeedsAttention = sortedDepartments.some((d) => d.needAttentionCount > 0)

  const options: IOption[] = sortedDepartments.map((d) => ({
    value: d.id,
    label: d.name,
    needAttentionCount: d.needAttentionCount,
  }))

  return (
    <Select<string, IOption>
      className="te-dept-select"
      classNames={{ popup: { root: 'te-dept-select__popup' } }}
      showSearch
      value={selectedId ?? undefined}
      onChange={onSelect}
      options={options}
      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      labelRender={({ label }) => (
        <span className="te-dept-select__label">
          <span className="te-dept-select__name">{label}</span>
          <span className="te-dept-select__count">
            {' · '}
            {formatMessage({ id: 'teamEnergy.departmentSelector.count' }, { count: departments.length })}
          </span>
        </span>
      )}
      optionRender={(oriOption) => {
        const data = oriOption.data as IOption
        return (
          <div className="te-dept-select__option">
            <span className="te-dept-select__option-name">{data.label}</span>
            {data.needAttentionCount > 0 && <span className="te-dept-select__option-badge">{data.needAttentionCount}</span>}
          </div>
        )
      }}
      popupRender={(menu) => (
        <>
          {hasNeedsAttention && (
            <div className="te-dept-select__section">
              <IntlMessages id="teamEnergy.departmentSelector.needsAttention" />
            </div>
          )}
          {menu}
        </>
      )}
      placeholder={formatMessage({ id: 'teamEnergy.departmentSelector.searchPlaceholder' })}
    />
  )
}

export default DepartmentSelector
